import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Final design pass (Bold + Rose). Variant styles are pointed at the real
// design tokens to match the prototype `Btn` atom, since the shadcn semantic
// aliases (--color-cotton-rose etc.) are not defined in tokens.css:
//   default  -> primary (navy fill / linen text, navy-hover)
//   secondary-> rose (rose fill / navy text, rose-dark hover)
//   outline  -> ghost (line-dark border, transparent, linen hover)
//   ghost    -> transparent, linen hover
//   link     -> navy text, underline on hover
// Radius/size and the shadcn API (variant/size/asChild) are unchanged.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-rose)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-white)] disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      variant: {
        default:
          'bg-[color:var(--color-navy)] text-[color:var(--color-linen)] hover:bg-[color:var(--color-navy-hover)]',
        destructive:
          'bg-[color:var(--color-error)] text-[color:var(--color-white)] hover:opacity-90',
        outline:
          'border-[1.5px] border-[color:var(--color-line-dark)] bg-transparent text-[color:var(--color-black)] hover:bg-[color:var(--color-linen)]',
        secondary:
          'bg-[color:var(--color-rose)] text-[color:var(--color-navy)] hover:bg-[color:var(--color-rose-dark)]',
        ghost:
          'bg-transparent text-[color:var(--color-black)] hover:bg-[color:var(--color-linen)]',
        link: 'text-[color:var(--color-navy)] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-[22px] py-2.5',
        sm: 'h-9 rounded-md px-3.5',
        lg: 'h-11 rounded-md px-[30px]',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
